// skymath.js, pure sidereal-time helpers for FIXED targets (planets / DSOs).
// Self-contained: mirrors the hand-rolled hour-angle math SkyPlotView uses for
// its altitude curves (kept duplicated for now; consolidation is a follow-up).
// No astronomy-engine dependency, relevant while its license question is open.

const DEG = Math.PI / 180;
const HRS = Math.PI / 12;            // radians per hour of RA / hour-angle
const SOLAR_PER_SIDEREAL = 1 / 1.00273790935; // solar hours per sidereal hour

// Greenwich Mean Sidereal Time (hours) for a JS epoch-ms instant.
// Standard IAU 1982 polynomial, good to arc-seconds.
export function gmstHours(ms) {
  const JD = ms / 86400000 + 2440587.5;
  const D  = JD - 2451545.0;
  const T  = D / 36525.0;
  let g = 280.46061837 + 360.98564736629 * D
        + 0.000387933 * T * T - (T * T * T) / 38710000.0;
  g = ((g % 360) + 360) % 360;
  return g / 15;
}

// True altitude AND azimuth (deg) of an RA/Dec object for an observer at ms.
export function altAzAt(raHours, decDeg, lat, lonDeg, ms) {
  const lst = (gmstHours(ms) + lonDeg / 15) % 24;
  let ha = (lst - raHours) % 24;
  if (ha < -12) ha += 24; else if (ha > 12) ha -= 24;
  const haRad = ha * HRS;
  const dec = decDeg * DEG, latR = lat * DEG;
  const sinAlt = Math.sin(dec) * Math.sin(latR)
               + Math.cos(dec) * Math.cos(latR) * Math.cos(haRad);
  const alt = Math.asin(Math.max(-1, Math.min(1, sinAlt)));
  // Azimuth from north, clockwise. Resolve the acos ambiguity with sin(HA):
  // positive hour angle = object west of the meridian.
  const cosAz = (Math.sin(dec) - Math.sin(alt) * Math.sin(latR))
              / (Math.cos(alt) * Math.cos(latR) || 1e-9);
  let az = Math.acos(Math.max(-1, Math.min(1, cosAz))) / DEG;
  if (Math.sin(haRad) > 0) az = 360 - az;
  return { alt: alt / DEG, az };
}

// Next meridian transit (epoch ms) at/after fromMs: the moment LST equals RA,
// i.e. the object is due south (north for circumpolar-north) and at peak alt.
export function nextTransit(raHours, lonDeg, fromMs) {
  const lst = (gmstHours(fromMs) + lonDeg / 15) % 24;
  let dH = (raHours - lst) % 24;
  if (dH < 0) dH += 24;
  return fromMs + dH * SOLAR_PER_SIDEREAL * 3600e3;
}

// Peak altitude at transit (deg): 90 - |lat - dec|, clamped.
export function peakAltDeg(decDeg, lat) {
  return Math.max(0, Math.min(90, 90 - Math.abs(lat - decDeg)));
}

// The window around transit during which the object sits above minAlt.
// Returns { kind:'window', t0, t1 } | { kind:'always' } | { kind:'never' }.
export function windowAbove(raHours, decDeg, lat, lonDeg, minAltDeg, transitMs) {
  const dec = decDeg * DEG, latR = lat * DEG, minA = minAltDeg * DEG;
  const denom = Math.cos(latR) * Math.cos(dec);
  if (Math.abs(denom) < 1e-9) return { kind: 'never' };
  const cosHA = (Math.sin(minA) - Math.sin(latR) * Math.sin(dec)) / denom;
  if (cosHA > 1)  return { kind: 'never' };   // never reaches minAlt
  if (cosHA < -1) return { kind: 'always' };  // circumpolar above minAlt
  const haH = (Math.acos(cosHA) / HRS) * SOLAR_PER_SIDEREAL; // solar hours
  return { kind: 'window', t0: transitMs - haH * 3600e3, t1: transitMs + haH * 3600e3 };
}